<template>
  <v-card flat>
    <v-card-text>
      <!-- ================================================================= -->
      <!-- HEADER & MAIN LIST                                              -->
      <!-- ================================================================= -->
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="headline">Fahrzeuge und Checklisten</h3>
        <v-btn color="default" dark @click="dialog.createVehicle = true">
          <v-icon left>mdi-plus</v-icon> Fahrzeug erstellen
        </v-btn>
      </div>

      <div v-if="!vehicles || vehicles.length === 0" class="mt-4">
        <v-alert type="info">Es wurden noch keine Fahrzeuge erstellt.</v-alert>
      </div>

      <v-expansion-panels v-else v-model="panel" focusable>
        <!-- Draggable wrapper for sorting the vehicle panels -->
        <draggable v-model="vehicles" handle=".drag-handle" ghost-class="ghost" @end="saveVehicleOrder" class="full-width-draggable">
          <v-expansion-panel v-for="vehicle in vehicles" :key="vehicle._id">
            <v-expansion-panel-header>
              <div class="d-flex align-center" style="width: 100%;">
                <v-icon class="drag-handle mr-4" title="Fahrzeuge sortieren">mdi-drag-vertical</v-icon>
                <span class="font-weight-bold">{{ vehicle.name }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <!--
                REMOVED: The inner <draggable> component is gone.
                This is now a standard v-list that respects the sort order from the API.
              -->
              <v-list dense>
                <div v-if="!vehicle.lists || vehicle.lists.length === 0" class="text-center pa-2 grey--text">
                  Für dieses Fahrzeug gibt es noch keine Checklisten.
                </div>
                <template v-for="(list, index) in vehicle.lists">
                  <div :key="list._id">
                    <v-list-item>
                      <!-- REMOVED: The inner drag handle icon is gone. -->
                      <v-list-item-content>
                        <v-list-item-title>{{ list.title }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="d-flex">
                        <v-btn small text color="primary" @click="showChecklist(list)">Items</v-btn>
                        <v-btn small text color="secondary" @click="editChecklist(list)">Bearbeiten</v-btn>
                        <v-btn small text color="red" @click="confirmDelete(list)">Löschen</v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index < vehicle.lists.length - 1"></v-divider>
                  </div>
                </template>
              </v-list>

              <v-divider class="my-4"></v-divider>
              <v-card-actions>
                <v-btn text color="success" @click="openChecklistDialog(vehicle)"><v-icon left>mdi-plus</v-icon> Checkliste erstellen</v-btn>
                <v-spacer></v-spacer>
                <v-btn text color="secondary" @click="editVehicle(vehicle)">Fahrzeug umbenennen</v-btn>
                <v-btn text color="red" @click="confirmVehicleDelete(vehicle)">Fahrzeug löschen</v-btn>
              </v-card-actions>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </draggable>
      </v-expansion-panels>

    </v-card-text>

    <!-- ================================================================= -->
    <!-- DIALOGS (No changes here)                                       -->
    <!-- ================================================================= -->
    <VehicleDialog v-model="dialog.createVehicle" @save="addVehicle" />
    <VehicleDialog v-model="dialog.editVehicle" :isEditing="true" :vehicleName="editVehicleName" @save="saveVehicle" />
    <ChecklistDialog v-model="dialog.createChecklist" :vehicleName="vehicle ? vehicle.name : ''" @save="createChecklist" />
    <ChecklistDialog v-model="dialog.editChecklist" :isEditing="true" :checklistData="editableChecklist" @save="saveChecklist" />
    <ConfirmDialog v-model="dialog.confirmVehicleDelete" type="Fahrzeug" :name="vehicle ? vehicle.name : ''" @confirm="deleteVehicle" />
    <ConfirmDialog v-model="dialog.confirmDelete" type="Checkliste" :name="selectedList ? selectedList.title : ''" @confirm="deleteChecklist" />
    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-card>
</template>

<style>
/* Style for the draggable wrapper to ensure it's full width */
.full-width-draggable {
  width: 100%;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.drag-handle {
  cursor: move;
}
/* REMOVED: .drag-handle-inner style is no longer needed */
</style>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import VehicleDialog from '@/components/admin/dialogs/VehicleDialog.vue';
import ChecklistDialog from '@/components/admin/dialogs/ChecklistDialog.vue';
import ConfirmDialog from '@/components/admin/dialogs/ConfirmDialog.vue';

export default {
  name: "VehicleManagement",
  components: {
    draggable,
    VehicleDialog,
    ChecklistDialog,
    ConfirmDialog,
  },
  data() {
    return {
      vehicles: [],
      panel: null, // Controls which panel is open
      vehicle: null,
      selectedList: null,
      editVehicleName: '',
      editableChecklist: {},
      dialog: {
        createVehicle: false, editVehicle: false,
        createChecklist: false, editChecklist: false,
        confirmDelete: false, confirmVehicleDelete: false,
      },
      snack: { state: false, color: 'success', timeout: 3000, message: '' }
    }
  },
  mounted() {
    this.fetchVehicles();
  },
  methods: {
    fetchVehicles() {
      axios.get('/vehicles')
          .then(response => { this.vehicles = response.data.vehicles; })
          .catch(() => this.showSnackbar("Fehler beim Laden der Fahrzeuge.", 'error'));
    },

    saveVehicleOrder() {
      const updates = this.vehicles.map((vehicle, index) => ({
        _id: vehicle._id,
        sorter: index,
      }));
      axios.patch('/vehicles/order', updates)
          .then(() => this.showSnackbar("Fahrzeug-Reihenfolge gespeichert.", "success"))
          .catch(() => this.showSnackbar("Fehler beim Speichern der Fahrzeug-Reihenfolge.", "error"));
    },
    addVehicle(name) {
      axios.post('/vehicles', { name })
          .then(() => {
            this.showSnackbar("Fahrzeug erfolgreich erstellt.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Erstellen des Fahrzeugs.", 'error'));
    },
    editVehicle(vehicle) {
      this.vehicle = vehicle;
      this.editVehicleName = vehicle.name;
      this.dialog.editVehicle = true;
    },
    saveVehicle(name) {
      axios.patch(`/vehicles/${this.vehicle._id}`, { name })
          .then(() => {
            this.showSnackbar("Fahrzeug wurde gespeichert.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Speichern.", 'error'));
    },
    confirmVehicleDelete(vehicle) {
      this.vehicle = vehicle;
      this.dialog.confirmVehicleDelete = true;
    },
    deleteVehicle() {
      axios.delete(`/vehicles/${this.vehicle._id}`)
          .then(() => {
            this.showSnackbar("Fahrzeug wurde entfernt.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Löschen.", 'error'));
    },

    openChecklistDialog(vehicle) {
      this.vehicle = vehicle;
      this.dialog.createChecklist = true;
    },
    createChecklist({ title }) {
      axios.post('/checklists', { title, vehicle: this.vehicle._id })
          .then(() => {
            this.showSnackbar("Checkliste erfolgreich erstellt.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Erstellen der Checkliste.", 'error'));
    },
    editChecklist(list) {
      this.selectedList = list;
      this.editableChecklist = { title: list.title, sorter: list.sorter };
      this.dialog.editChecklist = true;
    },
    saveChecklist(data) {
      axios.patch(`/checklists/${this.selectedList._id}`, data)
          .then(() => {
            this.showSnackbar("Checkliste wurde gespeichert.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Speichern.", 'error'));
    },
    confirmDelete(checklist) {
      this.selectedList = checklist;
      this.dialog.confirmDelete = true;
    },
    deleteChecklist() {
      axios.delete(`/checklists/${this.selectedList._id}`)
          .then(() => {
            this.showSnackbar("Checkliste wurde entfernt.");
            this.fetchVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Löschen.", 'error'));
    },
    showChecklist(checklist) {
      this.$router.push({ name: 'ChecklistCreator', params: { id: checklist._id } });
    },

    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
}
</script>